import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  FileText,
  Search,
  Compass,
  Bookmark,
  Handshake,
  MessagesSquare,
  ShieldCheck,
  FileCheck2,
  Megaphone,
  Target,
  Users,
  Bell,
  Store,
  ClipboardCheck,
  LogOut,
  UserPlus,
  Plus,
  ArrowRight,
  Menu,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SpnLogo } from "@/components/brand/spn-logo";
import {
  useGetMe,
  useLogout,
  useGetUnreadCounts,
  getGetUnreadCountsQueryKey,
  useListAccessRequests,
  getListAccessRequestsQueryKey,
  useSwitchTenant,
  useListMyPendingTenantInvites,
  getListMyPendingTenantInvitesQueryKey,
  useAcceptPendingTenantInvite,
} from "@workspace/api-client-react";
import { Badge } from "@/components/ui/badge";
import { TenantSwitcher, CreateTenantDialog } from "./tenant-switcher";
import { VerifiedBadge } from "@/components/verified-badge";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { TourProvider } from "@/components/tour/tour-provider";
import { HelpMenu } from "@/components/tour/help-menu";
import { FirstLoginPrompt } from "@/components/tour/first-login-prompt";
import {
  WorkspaceAccessRequestPanel,
  WorkspaceAccessRequestStatusList,
} from "@/components/workspace-access-request";

type BadgeKind = "messages" | "teaming" | "inviteConversions" | "tenantInvites" | "accessRequests";

type NavItem = {
  href: string;
  label: string;
  icon: typeof LayoutDashboard;
  adminOnly?: boolean;
  tenantAdminOnly?: boolean;
  ownerOnly?: boolean;
  engagementOnly?: boolean;
  badge?: BadgeKind;
};

const NAV: NavItem[] = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/vendor-profile", label: "Vendor Profile", icon: Building2 },
  { href: "/agency-experience", label: "Agency Experience", icon: Briefcase },
  { href: "/documents", label: "Documents", icon: FileText },
  { href: "/search", label: "Search", icon: Search },
  { href: "/discover", label: "Discover Vendors", icon: Compass },
  { href: "/watchlist", label: "My Watchlist", icon: Bookmark, badge: "inviteConversions" },
  { href: "/opportunities", label: "Opportunity Matches", icon: Target },
  { href: "/partners", label: "Partners", icon: Users },
  { href: "/teaming", label: "Teaming", icon: Handshake, badge: "teaming" },
  { href: "/team-workspaces", label: "Team Workspaces", icon: ClipboardCheck },
  { href: "/messages", label: "Messages", icon: MessagesSquare, badge: "messages" },
  { href: "/marketplace", label: "Marketplace", icon: Store },
  { href: "/submissions", label: "My Submissions", icon: ClipboardCheck },
  { href: "/settings/notifications", label: "Notifications", icon: Bell },
  {
    href: "/settings/team",
    label: "Team",
    icon: UserPlus,
    tenantAdminOnly: true,
    badge: "tenantInvites",
  },
  { href: "/admin/engagement", label: "Engagement", icon: Megaphone, engagementOnly: true },
  { href: "/admin", label: "Admin", icon: ShieldCheck, adminOnly: true, badge: "accessRequests" },
  { href: "/admin/concierge", label: "Concierge setup", icon: FileCheck2, adminOnly: true },
  { href: "/vendor-review", label: "Profile review", icon: FileCheck2, ownerOnly: true },
];

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [location, navigate] = useLocation();
  const qc = useQueryClient();
  const { data: me, isLoading, error } = useGetMe();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile nav drawer whenever the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location]);

  const logoutMut = useLogout({
    mutation: {
      onSuccess: () => {
        qc.clear();
        toast.success("Signed out");
        navigate("/");
      },
    },
  });

  useEffect(() => {
    const status = (error as { status?: number } | null)?.status;
    if (status === 401) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/login?next=${next}`);
    }
  }, [error, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-6 w-6 text-primary" />
      </div>
    );
  }

  if (!me) {
    return null;
  }

  if (!me.currentTenant) {
    return (
      <NoWorkspaceGate
        memberships={me.memberships}
        onSignOut={() => logoutMut.mutate()}
        signingOut={logoutMut.isPending}
      />
    );
  }

  const isAdmin = !!me.user.isPlatformAdmin;
  const isEngagement =
    isAdmin || !!(me.user as { isEngagementManager?: boolean }).isEngagementManager;
  const isTenantAdmin = me.currentRole === "tenant_admin" || me.currentRole === "owner" || isAdmin;
  const items = NAV.filter((n) => {
    if (n.adminOnly && !isAdmin) return false;
    if (n.tenantAdminOnly && !isTenantAdmin) return false;
    if (n.ownerOnly && me?.currentRole !== "owner") return false;
    if (n.engagementOnly && !isEngagement) return false;
    return true;
  });
  const hasTenant = !!me.currentTenant;
  const initialFirstLogin = !me.user.tourCompletedAt && !me.user.tourDismissedAt;

  const sidebarInner = (
    <>
      <div className="flex h-16 shrink-0 items-center gap-2 px-5 border-b border-sidebar-border">
        <SpnLogo className="h-8 w-8" />
        <div className="leading-tight">
          <div className="text-sm font-semibold">Synergistic</div>
          <div className="text-[10px] text-sidebar-foreground/60 uppercase tracking-wider">
            Partner Network
          </div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {items.map((item) => (
          <NavLink
            key={item.href}
            item={item}
            active={location === item.href || location.startsWith(item.href + "/")}
            hasTenant={hasTenant}
          />
        ))}
      </nav>
      <div className="p-3 text-[11px] text-sidebar-foreground/50 border-t border-sidebar-border">
        {me.currentRole ?? "no role"} · {me.user.email}
      </div>
    </>
  );

  return (
    <TourProvider initialFirstLogin={initialFirstLogin} userId={me.user.id}>
      <div className="flex min-h-screen bg-background">
        <aside className="hidden w-60 flex-col bg-sidebar text-sidebar-foreground lg:flex">
          {sidebarInner}
        </aside>

        <div className="flex min-w-0 flex-1 flex-col" data-testid="app-main-pane">
          <header className="flex h-16 items-center justify-between gap-2 border-b border-border bg-card px-3 sm:gap-4 sm:px-6">
            <div className="flex min-w-0 items-center gap-1.5 sm:gap-3">
              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 lg:hidden"
                    aria-label="Open navigation menu"
                    data-testid="button-mobile-nav"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className="flex w-72 flex-col gap-0 bg-sidebar p-0 text-sidebar-foreground [&>button]:text-sidebar-foreground"
                >
                  <SheetTitle className="sr-only">Navigation</SheetTitle>
                  {sidebarInner}
                </SheetContent>
              </Sheet>
              <TenantSwitcher
                currentTenantName={me.currentTenant?.name ?? null}
                currentTenantId={me.currentTenant?.id ?? null}
              />
            </div>
            <div className="flex shrink-0 items-center gap-1 sm:gap-3">
              <div className="hidden text-right md:block">
                <div className="text-sm font-medium">{me.user.email}</div>
                {me.user.isPlatformAdmin && (
                  <div className="text-[11px] text-primary">Platform admin</div>
                )}
                {!me.user.isPlatformAdmin &&
                  (me.user as { isEngagementManager?: boolean }).isEngagementManager && (
                    <div className="text-[11px] text-primary">Engagement manager</div>
                  )}
              </div>
              <HelpMenu />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => logoutMut.mutate()}
                disabled={logoutMut.isPending}
                className="gap-2"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </header>
          <main className="flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8">{children}</div>
          </main>
        </div>
        <FirstLoginPrompt />
      </div>
    </TourProvider>
  );
}

function NavLink({
  item,
  active,
  hasTenant,
}: {
  item: NavItem;
  active: boolean;
  hasTenant: boolean;
}) {
  const Icon = item.icon;
  // Only fetch unread counts when there is an active workspace and the link
  // actually has an unread-style badge — keeps query fan-out minimal.
  const isUnreadBadge =
    item.badge === "messages" ||
    item.badge === "teaming" ||
    item.badge === "inviteConversions" ||
    item.badge === "tenantInvites";
  const { data } = useGetUnreadCounts({
    query: {
      enabled: hasTenant && isUnreadBadge,
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
      queryKey: getGetUnreadCountsQueryKey(),
    },
  });
  // Access-request badge is admin-only and driven by the admin list endpoint.
  const { data: pendingAccess } = useListAccessRequests(
    { status: "pending" },
    {
      query: {
        enabled: item.badge === "accessRequests",
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
        queryKey: getListAccessRequestsQueryKey({ status: "pending" }),
      },
    },
  );
  const count =
    item.badge === "messages"
      ? (data?.messages ?? 0)
      : item.badge === "teaming"
        ? (data?.teaming ?? 0)
        : item.badge === "inviteConversions"
          ? (data?.inviteConversions ?? 0)
          : item.badge === "tenantInvites"
            ? (data?.tenantInvites ?? 0)
            : item.badge === "accessRequests"
              ? (pendingAccess?.length ?? 0)
              : 0;
  return (
    <Link
      href={item.href}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
        active
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
      )}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{item.label}</span>
      {count > 0 && (
        <Badge
          variant="default"
          className="h-5 min-w-[20px] justify-center bg-primary px-1.5 text-[11px] leading-none text-primary-foreground"
          data-testid={`badge-${item.badge}`}
        >
          {count > 99 ? "99+" : count}
        </Badge>
      )}
    </Link>
  );
}

interface NoWorkspaceGateProps {
  memberships: ReadonlyArray<{
    tenantId: string;
    tenantName: string;
    role: string;
    verified: boolean;
  }>;
  onSignOut: () => void;
  signingOut: boolean;
}

function NoWorkspaceGate({ memberships, onSignOut, signingOut }: NoWorkspaceGateProps) {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const pendingInvites = useListMyPendingTenantInvites({
    query: {
      retry: false,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      queryKey: getListMyPendingTenantInvitesQueryKey(),
    },
  });
  const switchMut = useSwitchTenant({
    mutation: {
      onSuccess: () => {
        qc.removeQueries();
        qc.invalidateQueries();
      },
      onError: (e) => toast.error(e?.message ?? "Could not switch workspace"),
    },
  });
  const acceptInviteMut = useAcceptPendingTenantInvite({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries();
        toast.success("Joined workspace");
      },
      onError: (e) => toast.error(e?.message ?? "Could not join workspace"),
    },
  });

  const hasMemberships = memberships.length > 0;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center space-y-3">
          <SpnLogo className="mx-auto h-10 w-10" />
          <h1 className="text-2xl font-semibold tracking-tight">
            {hasMemberships
              ? "Choose your workspace"
              : "Welcome to SPN — let's set up your workspace"}
          </h1>
          <p className="text-sm text-muted-foreground">
            SPN is a B2B platform for GovCon teaming. Every workspace represents a real business
            with a SAM.gov UEI.
          </p>
        </div>

        {hasMemberships && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Continue to a workspace you belong to
            </p>
            <div className="space-y-2">
              {memberships.map((m) => (
                <button
                  key={m.tenantId}
                  type="button"
                  disabled={switchMut.isPending}
                  onClick={() => switchMut.mutate({ data: { tenantId: m.tenantId } })}
                  data-testid={`button-continue-tenant-${m.tenantId}`}
                  className="flex w-full items-center justify-between rounded-md border border-border bg-card px-4 py-3 text-left hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate font-medium">{m.tenantName}</span>
                    {m.verified && <VerifiedBadge verified showLabel={false} />}
                    <span className="text-xs text-muted-foreground">· {m.role}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-5">
          <div className="space-y-1">
            <h2 className="text-base font-semibold">Join an existing workspace</h2>
            <p className="text-sm text-muted-foreground">
              If a coworker invited you, choose that invitation below. You can also create your own
              workspace if you do not have an invitation.
            </p>
          </div>
          {pendingInvites.isLoading ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-card p-3 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4 text-primary" />
              Checking for invitations…
            </div>
          ) : pendingInvites.isError ? (
            <div
              role="status"
              className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            >
              We could not load invitations right now. You can refresh this page or create a new
              workspace.
            </div>
          ) : pendingInvites.data && pendingInvites.data.length > 0 ? (
            <div className="space-y-2" data-testid="pending-workspace-invites">
              {pendingInvites.data.map((invite) => (
                <div
                  key={invite.id}
                  className="rounded-md border border-border bg-card p-4"
                  data-testid={`pending-workspace-invite-${invite.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Building2
                      className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="font-medium">{invite.tenantName}</p>
                      <p className="text-sm text-muted-foreground">
                        Invited by {invite.invitedByEmail} ·{" "}
                        {invite.role === "tenant_admin" ? "Workspace admin" : "Member"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Invitation expires {new Date(invite.expiresAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    className="mt-3 w-full gap-2"
                    onClick={() => acceptInviteMut.mutate({ data: { inviteId: invite.id } })}
                    disabled={acceptInviteMut.isPending}
                    data-testid={`button-join-workspace-${invite.id}`}
                  >
                    {acceptInviteMut.isPending ? "Joining…" : `Join ${invite.tenantName}`}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-md border border-border bg-card p-3 text-sm text-muted-foreground">
              No pending workspace invitations were found for your sign-in email.
            </p>
          )}
        </div>

        <div className="space-y-4 rounded-lg border border-border bg-card p-5">
          <WorkspaceAccessRequestPanel />
          <WorkspaceAccessRequestStatusList />
        </div>

        <div className="space-y-3 rounded-lg border border-border bg-card p-5">
          <div className="space-y-1">
            <h2 className="text-base font-semibold">
              {hasMemberships
                ? "Or create a new workspace"
                : "Step 1 — Create your business workspace"}
            </h2>
            <p className="text-sm text-muted-foreground">
              You'll need your company's 12-character SAM.gov UEI. We'll look up your business
              automatically and you'll become the workspace admin.
            </p>
          </div>
          <Button
            size="lg"
            className="w-full gap-2"
            onClick={() => setCreateOpen(true)}
            data-testid="button-open-create-workspace"
          >
            <Plus className="h-4 w-4" />
            Create business workspace
          </Button>
          {!hasMemberships && (
            <ol className="space-y-1.5 pt-2 text-xs text-muted-foreground list-decimal pl-4">
              <li>Enter your SAM.gov UEI — we'll fetch your company details.</li>
              <li>Complete your vendor profile so partners can find you.</li>
              <li>Search for vendors and start teaming.</li>
            </ol>
          )}
        </div>

        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSignOut}
            disabled={signingOut}
            className="gap-2"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </div>

      <CreateTenantDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
